import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Heart, Loader2, Play, Star, VideoOff, X } from "lucide-react";

import { Avatar } from "@/components/ui/Avatar";
import { getSignedMeetupVideoUrl } from "@/features/video/upload";
import type { EventFeedRow } from "@/lib/api/participants";
import { trackPitchView } from "@/lib/api/pitch-views";
import { cn } from "@/lib/utils";

interface FeedCardProps {
  participant: EventFeedRow;
  matchReason: string | null;
  /** True when the organizer dropped this candidate into the viewer's
   *  feed directly — TZ block C item 14. Renders an "organizer
   *  recommendation" badge and slightly tints the card border so the
   *  source is visually unmistakable. */
  isRecommended?: boolean;
  liked: boolean;
  pending: boolean;
  disabled?: boolean;
  /** Hint shown after the very first like in this session. */
  tooltip?: string | null;
  onLike: () => void;
  /** Burn-through skip — removes the card from the current batch. */
  onSkip?: () => void;
  /** Viewer context — needed for the signed video URL. */
  viewerEventId: string;
  viewerParticipantId: string;
}

/**
 * Feed card matching the v6.05 redesign — large 200×200 circular pitch video
 * with the Finally signature blue-outline ring, a floating play button with
 * a pulsing halo, the duration chip pinned to the corner, the match-reason
 * pill below the video, identity row, info boxes, and a single full-width
 * Like CTA. Skip happens via the page-level pagination arrows; the card
 * itself no longer carries an inline skip button.
 */
export function FeedCard({
  participant,
  matchReason,
  isRecommended,
  liked,
  pending,
  disabled,
  tooltip,
  onLike,
  onSkip,
  viewerEventId,
  viewerParticipantId,
}: FeedCardProps) {
  const profile = participant.profile_json as
    | {
        headline?: string;
        about?: string;
        looking_for?: string | string[];
        can_help_with?: string | string[];
      }
    | null;

  const lookingFor = normalizeListValue(profile?.looking_for) || participant.looking_for;
  const canHelpWith = normalizeListValue(profile?.can_help_with) || participant.can_help_with;
  const about = profile?.about || profile?.headline;
  const roleLine = [participant.role, participant.company].filter(Boolean).join(" · ");
  const reason = matchReason ?? "Goals overlap with yours";
  const isReviewing = participant.pitch_status === "submitted";
  // Text-only card variant (TZ block A item 3): the participant is in
  // the matching pool with a complete profile but hasn't recorded a
  // pitch yet. We replace the live pitch circle with a deliberately
  // empty grey placeholder so the absence reads as "card not finished
  // yet". The placeholder is the strongest pull toward the Record
  // pitch CTA in our own banner.
  const isTextOnly = !participant.has_video;

  // Video URL — fetched eagerly so the <video> element below can request
  // just the first frame (preload="metadata", ~100-300 KB) and show it as
  // a still preview. Full bytes only download when the user taps Play —
  // see PitchCircle below.
  const { data: videoUrl } = useQuery({
    queryKey: [
      "feed-card-video",
      viewerEventId,
      viewerParticipantId,
      participant.video_storage_path ?? "",
    ],
    queryFn: () =>
      getSignedMeetupVideoUrl({
        eventId: viewerEventId,
        participantId: viewerParticipantId,
        targetPath: participant.video_storage_path ?? "",
      }),
    enabled: Boolean(participant.video_storage_path),
    staleTime: 4 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });

  return (
    <div className="flex w-full flex-col">
      <div
        className={cn(
          "flex flex-col overflow-hidden rounded-[20px] border bg-white shadow-[0_2px_16px_rgba(0,0,0,0.06)]",
          isRecommended
            ? "border-[#f3e0a6] ring-1 ring-[#fdf3e2]"
            : "border-[#e2e8f0]",
        )}
      >
        {/* V3 spec frame 03b: amber star badge as the trust signal.
            Replaces the v1 blue sparkles — the warmer palette reads
            as "the organizer picked this person" rather than "system
            highlight". */}
        {isRecommended ? (
          <div className="flex items-center gap-1.5 border-b border-[#f3e0a6] bg-[#fdf3e2] px-5 py-2">
            <Star
              className="h-3.5 w-3.5 fill-[#b4690e] text-[#b4690e]"
              strokeWidth={0}
            />
            <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#b4690e]">
              Recommended by organizer
            </span>
          </div>
        ) : null}
        {/* Video area — circle + match reason. The bottom margin on the
            circle wrap (mb-9) reserves room for the floating play button
            and the duration / review chip that hang below the circle. */}
        <div className="flex flex-col items-center gap-4 px-5 pt-6 pb-4">
          {isTextOnly ? (
            <TextCardPlaceholder participantName={participant.name} />
          ) : (
            <PitchCircle
              videoUrl={videoUrl}
              participantName={participant.name}
              isReviewing={isReviewing}
              hasVideoPath={Boolean(participant.video_storage_path)}
              onFirstPlay={() =>
                void trackPitchView(viewerParticipantId, participant.id)
              }
            />
          )}

          <div className="inline-flex max-w-[300px] items-center gap-1.5 rounded-full border border-[#bfdbfe] bg-[#eff6ff] px-3.5 py-1.5 text-center">
            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#2563eb]" />
            <span className="text-[12px] font-semibold leading-[1.4] text-[#2563eb]">
              {reason}
            </span>
          </div>
        </div>

        {/* Identity */}
        <div className="px-5">
          <p className="truncate text-[18px] font-bold leading-tight tracking-[-0.3px] text-[#1a1a1a]">
            {participant.name}
          </p>
          {roleLine ? (
            <p className="mt-1 truncate text-[13px] text-[#6c757d]">{roleLine}</p>
          ) : null}
        </div>

        {/* Info blocks — natural height, no inner scroll. The card grows
            to fit its content; the page-level scroll handles long feeds. */}
        <div className="flex flex-col gap-2.5 px-5 pt-3.5">
          {lookingFor ? (
            <InfoBox tone="amber" label="Looking for" text={lookingFor} />
          ) : null}
          {canHelpWith ? (
            <InfoBox tone="plain" label="Can help with" text={canHelpWith} />
          ) : null}
          {about ? <InfoBox tone="plain" label="About" text={about} /> : null}
        </div>

        {tooltip ? (
          <div className="mx-5 mt-3 rounded-[10px] border border-[#bfdbfe] bg-[#eff6ff] px-3 py-2 text-[12px] text-[#1e3a8a]">
            Like sent ✓ — if {tooltip} likes you back, contact info opens 2 hours before
            the event.
          </div>
        ) : null}

        {/* Footer — Like CTA only. Telegram (and any other direct contact)
            stays hidden until both sides have liked each other and the
            event's reveal window has opened. The Matches page is the
            single place that surfaces another participant's contact;
            doing so here would let anyone reach anyone without the
            mutual-interest gate. The Feed-level "Message in Telegram"
            shortcut was a regression and has been removed — see migration
            20260518184500_meetup_feed_hide_contact_until_match.sql. */}
        <div className="flex gap-2 px-5 py-5">
          {onSkip ? (
            <button
              type="button"
              onClick={onSkip}
              disabled={liked || pending}
              aria-label="Skip this person"
              className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[12px] border border-[#e2e8f0] bg-white text-[#475569] transition-colors hover:bg-[#f8fafc] disabled:opacity-50"
            >
              <X className="h-5 w-5" strokeWidth={2.2} />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onLike}
            disabled={liked || pending || disabled}
            className={cn(
              "flex h-[52px] flex-1 items-center justify-center gap-2 rounded-[12px] text-[15px] font-bold tracking-[-0.1px] transition-transform active:scale-[0.99]",
              liked
                ? "border border-[#a7f3d0] bg-[#f0fdf4] text-[#065f46]"
                : disabled
                  ? "bg-[#cbd5e1] text-white"
                  : "bg-[#2563eb] text-white shadow-[0_3px_12px_rgba(37,99,235,0.28)] hover:bg-[#1d4ed8]",
            )}
          >
            <Heart
              className={cn("h-4 w-4", liked ? "text-[#10b981]" : "")}
              fill={liked ? "currentColor" : "none"}
              strokeWidth={2}
            />
            {liked
              ? "Liked"
              : pending
                ? "Saving…"
                : disabled
                  ? "Complete profile first"
                  : "Like"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Text-only card variant (TZ block A item 3 + 8). Drops the live pitch
 * circle entirely and replaces it with a deliberately empty grey ring
 * around the initials avatar so the missing face reads as an
 * unfinished card. A small
 * "No video yet" badge sits where the duration chip sits on a video
 * card, so the absence reads as an unfinished card, not a styling
 * choice. Same dimensions as PitchCircle so the card height matches
 * across both variants.
 */
function TextCardPlaceholder({ participantName }: { participantName: string }) {
  return (
    <div className="relative mb-9 h-[200px] w-[200px] flex-shrink-0">
      <div
        className={cn(
          "relative grid h-[200px] w-[200px] place-items-center overflow-hidden rounded-full bg-[#f1f5f9]",
          // Muted, slightly desaturated ring so it doesn't compete with
          // the bright blue video ring used on full cards.
          "shadow-[0_0_0_3px_white,0_0_0_6px_#cbd5e1,0_4px_24px_rgba(15,23,42,0.06)]",
        )}
      >
        <Avatar
          name={participantName}
          size={140}
          className="h-[140px] w-[140px] opacity-90"
        />
      </div>
      <span className="absolute -bottom-5 left-1/2 z-[3] inline-flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-[6px] border border-[#e2e8f0] bg-[#f8fafc] px-2.5 py-1 text-[11px] font-bold tracking-[0.3px] text-[#94a3b8]">
        <VideoOff className="h-3 w-3" />
        No video yet
      </span>
    </div>
  );
}

interface PitchCircleProps {
  videoUrl: string | undefined;
  participantName: string;
  isReviewing: boolean;
  hasVideoPath: boolean;
  /** Fired exactly once per FeedCard lifetime, on the first play. */
  onFirstPlay?: () => void;
}

/**
 * 200×200 circular pitch video with the Finally signature ring (3px white
 * inner border + 3px primary outer outline, both implemented via stacked
 * box-shadows so they respect the radius), a floating play button below
 * with a pulsing halo, and a duration / review chip hanging at the corner.
 *
 * Falls back to an avatar gradient when no video path is present, and to a
 * spinner while the signed URL resolves.
 */
function PitchCircle({
  videoUrl,
  participantName,
  isReviewing,
  hasVideoPath,
  onFirstPlay,
}: PitchCircleProps) {
  const playReportedRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  // The video element starts in metadata-only mode (preload="metadata"),
  // which is enough to render a still first-frame preview but doesn't
  // download the full file. The first Play tap upgrades it to a real load.
  const [requestedPlay, setRequestedPlay] = useState(false);
  const [readyToPlay, setReadyToPlay] = useState(false);
  const [duration, setDuration] = useState<number | null>(null);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (!playing) {
      setRequestedPlay(true);
      // Triggers a full preload + play once the canplay event fires (see
      // onCanPlay below). Calling play() right away works on most browsers
      // even when only metadata is buffered, but iOS Safari sometimes
      // returns a rejected promise; we silently ignore it.
      v.play().catch(() => {
        /* swallow — onCanPlay will retry */
      });
    } else {
      v.pause();
    }
  };

  // When the video first reports it can play through and the user has
  // tapped, kick it off. Without this iOS Safari needs a second tap.
  useEffect(() => {
    if (!requestedPlay || !readyToPlay) return;
    const v = videoRef.current;
    if (v && v.paused) void v.play().catch(() => {});
  }, [requestedPlay, readyToPlay]);

  return (
    <div className="relative mb-9 h-[200px] w-[200px] flex-shrink-0">
      <button
        type="button"
        onClick={togglePlay}
        onContextMenu={(e) => e.preventDefault()}
        disabled={!hasVideoPath || !videoUrl}
        aria-label={
          !hasVideoPath
            ? "No pitch"
            : !videoUrl
              ? "Loading preview"
              : playing
                ? "Pause pitch"
                : "Play pitch"
        }
        className={cn(
          "relative block h-[200px] w-[200px] overflow-hidden rounded-full bg-[#1e293b]",
          "shadow-[0_0_0_3px_white,0_0_0_6px_#2563eb,0_4px_24px_rgba(37,99,235,0.18),0_2px_8px_rgba(0,0,0,0.10)]",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb] focus-visible:ring-offset-[6px]",
        )}
      >
        {videoUrl ? (
          <video
            ref={videoRef}
            // `#t=0.1` is a client-side media fragment — it never goes to
            // Supabase Storage, it just nudges the browser to decode and
            // paint a real first frame. Without it iOS Safari often shows
            // a black square next to `preload="metadata"`.
            src={videoUrl.includes("#t=") ? videoUrl : `${videoUrl}#t=0.1`}
            playsInline
            muted
            // Metadata-only initially: browser fetches the header + a
            // chunk that's enough to render the first frame as a still
            // preview (~100-300 KB depending on codec). Full bytes only
            // arrive after the user taps Play — see onClick + onCanPlay.
            preload="metadata"
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              const d = v.duration;
              if (Number.isFinite(d) && d > 0) setDuration(d);
              // Belt-and-braces seek so Chrome variants that ignore the
              // fragment also decode the first frame.
              try {
                if (v.currentTime < 0.05) v.currentTime = 0.1;
              } catch {
                /* metadata-only streams may reject seek — ignore. */
              }
            }}
            onCanPlay={() => setReadyToPlay(true)}
            onPlay={() => {
              setPlaying(true);
              // Once playing, unmute so the user actually hears the pitch.
              if (videoRef.current) videoRef.current.muted = false;
              // Report the view once per card lifetime — the server-
              // side RPC dedups by day on top of this, so a flicker
              // of repeated play events would also be harmless.
              if (!playReportedRef.current) {
                playReportedRef.current = true;
                onFirstPlay?.();
              }
            }}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            className="block h-full w-full object-cover"
          />
        ) : hasVideoPath ? (
          // Signed URL still resolving — brief blink only.
          <div className="grid h-full w-full place-items-center text-white/70">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          // No video on this participant — fall back to initials.
          <Avatar name={participantName} size={200} className="h-full w-full" />
        )}
      </button>

      {/* Floating play button — visible whenever the card has a pitch
          available *and* isn't currently playing. */}
      {videoUrl && !playing ? (
        <button
          type="button"
          onClick={togglePlay}
          aria-hidden="true"
          tabIndex={-1}
          className={cn(
            "absolute -bottom-5 left-1/2 z-10 grid h-12 w-12 -translate-x-1/2 place-items-center",
            "rounded-full border-[3px] border-white bg-[#2563eb] shadow-[0_4px_16px_rgba(37,99,235,0.35)]",
            "transition hover:bg-[#1d4ed8] disabled:opacity-80",
          )}
        >
          {requestedPlay && !readyToPlay ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-white" />
          ) : (
            <Play
              className="ml-0.5 h-3.5 w-3.5 fill-white text-white"
              strokeWidth={0}
            />
          )}
          <span
            className="pointer-events-none absolute -inset-[5px] rounded-full border-2 border-[#2563eb]/35 motion-safe:animate-ping"
            style={{ animationDuration: "2s" }}
          />
        </button>
      ) : null}

      {/* Bottom-right chip: Review while the pitch is still under
          moderation, otherwise the auto-detected duration. The two are
          mutually exclusive — a Review badge surfaces the more important
          signal to the viewer. */}
      {isReviewing ? (
        <span className="absolute -bottom-5 right-4 z-[3] whitespace-nowrap rounded-[6px] border border-[#e2e8f0] bg-[#f8fafc] px-2.5 py-1 text-[11px] font-bold tracking-[0.3px] text-[#94a3b8]">
          Review
        </span>
      ) : duration != null ? (
        <span className="absolute -bottom-5 right-4 z-[3] whitespace-nowrap rounded-[6px] border border-[#e2e8f0] bg-[#f8fafc] px-2.5 py-1 text-[11px] font-bold tracking-[0.3px] text-[#475569]">
          {formatDuration(duration)}
        </span>
      ) : null}
    </div>
  );
}

interface InfoBoxProps {
  tone: "amber" | "plain";
  label: string;
  text: string;
}

function InfoBox({ tone, label, text }: InfoBoxProps) {
  if (tone === "amber") {
    return (
      <div className="rounded-r-[6px] border-l-[3px] border-[#f59e0b] bg-[#fffbeb] px-3 py-2.5">
        <p className="text-[10px] font-bold uppercase tracking-[0.5px] text-[#92400e]">
          {label}
        </p>
        <p className="mt-1 whitespace-pre-line text-[13px] leading-[1.5] text-[#78350f]">
          {text}
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-[8px] border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5">
      <p className="text-[10px] font-bold uppercase tracking-[0.5px] text-[#94a3b8]">
        {label}
      </p>
      <p className="mt-1 whitespace-pre-line text-[13px] leading-[1.5] text-[#1a1a1a]">
        {text}
      </p>
    </div>
  );
}

function normalizeListValue(value: string | string[] | undefined): string {
  if (!value) return "";
  if (Array.isArray(value)) return value.map((v) => `· ${v}`).join("\n");
  return value;
}

function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
